import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, Mail } from "lucide-react";

/**
 * /about — the story page.
 *
 * Single, scrollable, minimal. Mirrors the museum aesthetic. Ends with a
 * contact email so press / partnerships / curious early users have a real
 * surface to reach. No forms, no chatbot — that breaks the trust posture.
 */
const SECTIONS = [
    {
        eyebrow: "the moment",
        body: "There's a 4-second pause between feeling something and saying it. MoodCard lives in that pause. Type what you feel; get three honest ways to say it back.",
    },
    {
        eyebrow: "the posture",
        body: "We don't reward streaks. We don't quantify your emotional life. We don't sell access to it. Every card is a finished thought, ephemeral by default. Some you'll keep. Most you won't.",
    },
    {
        eyebrow: "the moat",
        body: "Three cards a feeling. One link, two opens, a recipient. A correspondence. Cards stack like letters. We never charge for the moment — only for the materials.",
    },
];

export default function About() {
    return (
        <div className="relative min-h-screen w-full aurora-wash" data-testid="about-screen">
            <Helmet>
                <title>{`About — MoodCard`}</title>
                <meta name="description" content="MoodCard turns feelings into beautiful, shareable cards. A product of Zephiron Group LLC, Wyoming." />
                <meta property="og:title" content="About MoodCard" />
                <meta property="og:description" content="Three honest ways to say what you're feeling. A quiet place for a loud feeling." />
            </Helmet>

            <div className="absolute inset-0 grain pointer-events-none" />

            {/* Top bar — single back link, no clutter */}
            <header className="sticky top-0 z-10 max-w-md mx-auto px-5 pt-5 pb-3 backdrop-blur-md"
                style={{ backgroundColor: "color-mix(in srgb, var(--surface-1) 78%, transparent)" }}
            >
                <Link
                    to="/"
                    data-testid="about-back"
                    className="btn-press inline-flex items-center gap-2 font-sans text-[10px] uppercase tracking-[0.32em] text-app-3 hover:text-app transition-colors"
                >
                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                    back
                </Link>
            </header>

            <main className="relative z-10 max-w-md mx-auto px-6 pt-6 pb-24">
                {/* Brand pill — same green-outlined square as the home header
                    so the lineage is immediately legible. */}
                <div className="flex items-center gap-2 mb-10" data-testid="about-brand-pill">
                    <span
                        className="flex items-center justify-center h-5 w-5 rounded-[4px]"
                        style={{ border: "1.5px solid #34D399", boxShadow: "0 0 8px rgba(52, 211, 153, 0.18)" }}
                    >
                        <span className="block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#34D399" }} />
                    </span>
                    <span className="font-sans text-xs uppercase tracking-[0.35em] text-app">
                        MOODCARD
                    </span>
                </div>

                {/* Hero line — single phrase, deliberately quiet. */}
                <motion.h1
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="font-serif italic text-app text-3xl sm:text-4xl leading-snug mb-12"
                    data-testid="about-hero"
                >
                    A quiet place for a loud feeling.
                </motion.h1>

                {/* Story sections — eyebrow + paragraph, generous spacing. */}
                <div className="space-y-10" data-testid="about-sections">
                    {SECTIONS.map((s, i) => (
                        <motion.section
                            key={s.eyebrow}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                            data-testid={`about-section-${i}`}
                        >
                            <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3 mb-3">
                                {s.eyebrow}
                            </p>
                            <p className="font-serif text-app-2 text-[17px] leading-relaxed">
                                {s.body}
                            </p>
                        </motion.section>
                    ))}
                </div>

                {/* Contact — single email, museum-quiet */}
                <motion.section
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-14 pt-10 border-t border-app/15"
                    data-testid="about-contact"
                >
                    <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3 mb-3">
                        write to us
                    </p>
                    <a
                        href="mailto:hello@moodcard.app"
                        data-testid="about-email-link"
                        className="btn-press inline-flex items-center gap-2 font-serif italic text-app text-[19px] hover:text-amber-200 transition-colors"
                    >
                        <Mail className="w-4 h-4" strokeWidth={1.5} />
                        hello@moodcard.app
                    </a>
                    <p className="mt-3 font-sans text-[11px] text-app-3 leading-relaxed">
                        Press, partnerships, the occasional thank-you note. We read everything; we reply to most.
                    </p>
                </motion.section>

                {/* Legal stamp — same posture as the home footer. */}
                <footer className="mt-16 text-center">
                    <p className="font-sans text-[9px] uppercase tracking-[0.32em] text-app-3 opacity-65">
                        moodcard · a product of <span className="text-app-2">zephiron group llc</span> · wyoming, usa
                    </p>
                    <p className="mt-2 font-sans text-[9px] uppercase tracking-[0.28em] text-app-3 opacity-50">
                        © {new Date().getFullYear()} zephiron group llc · all rights reserved
                    </p>
                </footer>
            </main>
        </div>
    );
}
